import React from 'react';
import { Table, Space, Button, Popconfirm, message } from 'antd';
import { EditOutlined, DeleteOutlined, PrinterOutlined } from '@ant-design/icons';
import axios from 'axios';
import { API_BASE_URL } from '../constants/api';
import dayjs from 'dayjs';

const InvoiceTable = ({ dataSource, loading, onEdit, onDelete, accounts = [] }) => {
  const handlePrint = async (record) => {
    try {
      const response = await axios.get(`${API_BASE_URL}Invoice/GetById/${record.id}`);
      if (response.data.code === 0 && response.data.data) {
        const invoice = response.data.data;
        const account = accounts.find(a => a.id === invoice.clientAccountId);
        generateInvoicePDF(invoice, account);
      } else {
        message.error('فشل في جلب بيانات الفاتورة');
      }
    } catch (error) {
      message.error('خطأ في جلب بيانات الفاتورة');
    }
  };

  const generateInvoicePDF = (invoice, account) => {
    const items = invoice.items || [];
    const vouchers = invoice.vouchers || [];

    const invoiceTotalBeforeDiscounts = items.reduce((sum, item) => sum + (item.unitPrice * item.amount), 0);
    const totalItemsDiscount = items.reduce((sum, item) => sum + (item.discount || 0), 0);
    const invoiceOverallDiscount = invoice.discount || 0;
    const combinedTotalDiscount = totalItemsDiscount + invoiceOverallDiscount;
    const totalAfterAllDiscounts = invoiceTotalBeforeDiscounts - combinedTotalDiscount;

    const voucherTotal = vouchers.reduce((sum, v) => {
      const usdValue = v.value * (v.factor || 1);
      return sum + usdValue;
    }, 0);

    const remaining = totalAfterAllDiscounts - voucherTotal;

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      message.error('يرجى السماح بفتح النوافذ المنبثقة للطباعة');
      return;
    }

    const html = `
    <!DOCTYPE html>
    <html dir="rtl" lang="ar">
    <head>
    <meta charset="UTF-8">
    <title>فاتورة رقم ${invoice.number || invoice.id}</title>
    <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: 'Segoe UI', Tahoma, sans-serif; padding: 20px; color: #333; font-size: 12px; }
    .section { margin-bottom: 15px; }
    .separator { border: none; border-top: 1px solid #999; margin: 15px 0; }
    .header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; }
    .header-item { flex: 1; text-align: center; }
    .header-item span { font-weight: bold; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 10px; }
    th, td { border: 1px solid #ccc; padding: 6px 8px; text-align: center; }
    th { background: #f0f0f0; font-weight: bold; }
    .footer-row { background: #f9f9f9; font-weight: bold; }
    .result-section { background: #f5f5f5; padding: 10px; border-radius: 4px; }
    .result-row { display: flex; justify-content: space-between; padding: 4px 0; }
    .result-row.total { font-weight: bold; font-size: 14px; border-top: 1px solid #999; padding-top: 8px; margin-top: 8px; }
    @media print { body { padding: 0; } }
    </style>
    </head>
    <body>
    <div class="section">
    <div class="header">
      <div class="header-item"><span>الحساب:</span> ${account?.name || '-'}</div>
      <div class="header-item"><span>تاريخ الفاتورة:</span> ${invoice.createdDate ? dayjs(invoice.createdDate).format('YYYY-MM-DD') : '-'}</div>
      <div class="header-item"><span>تاريخ الطباعة:</span> ${new Date().toLocaleString('ar')}</div>
    </div>
    </div>

    <hr class="separator">

    <div class="section">
    <table>
      <thead>
        <tr>
          <th>#</th>
          <th>اسم المادة</th>
          <th>الكمية</th>
          <th>السعر</th>
          <th>المبلغ</th>
          </tr>
          </thead>
          <tbody>
          ${items.map((item, i) => {
          const lineTotal = (item.unitPrice || 0) * (item.amount || 0);
          return `<tr>
            <td>${i + 1}</td>
            <td>${item.materialName || '-'}</td>
            <td>${item.amount || 0}</td>
            <td>${(item.unitPrice || 0).toFixed(2)}</td>
            <td>${lineTotal.toFixed(2)}</td>
          </tr>`;
          }).join('')}
          <tr class="footer-row">
          <td colspan="1">عدد السجلات: ${items.length}</td>
          <td colspan="1">المجموع: ${invoiceTotalBeforeDiscounts.toFixed(2)}</td>

          <td colspan="2">الخصم الكلي: ${combinedTotalDiscount.toFixed(2)}</td>

          <td colspan="1">الإجمالي: ${totalAfterAllDiscounts.toFixed(2)}</td>
          </tr>      </tbody>
    </table>
    </div>

    <hr class="separator">

    ${vouchers.length > 0 ? `
    <div class="section">
    <table>
      <thead>
        <tr>
          <th>#</th>
          <th>المبلغ</th>
          <th>القيمة (USD)</th>
          <th>التاريخ</th>
          <th>ملاحظات</th>
        </tr>
      </thead>
      <tbody>
        ${vouchers.map((v, i) => {
          const usdValue = v.value * (v.factor || 1);
          return `<tr>
            <td>${i + 1}</td>
            <td>${v.value.toFixed(2)}</td>
            <td>${usdValue.toFixed(2)}</td>
            <td>${v.date ? dayjs(v.date).format('YYYY-MM-DD') : '-'}</td>
            <td>${v.notes || '-'}</td>
          </tr>`;
        }).join('')}
        <tr class="footer-row">
          <td colspan="2">مجموع الدفعات (USD)</td>
          <td colspan="3">${voucherTotal.toFixed(2)}</td>
        </tr>
      </tbody>
    </table>
    </div>

    <hr class="separator">
    ` : ''}

    <div class="result-section">
    <div class="result-row">
      <span>إجمالي الفاتورة</span>
      <span>${totalAfterAllDiscounts.toFixed(2)}</span>
    </div>
    <div class="result-row">
      <span>إجمالي الدفعات</span>
      <span>${voucherTotal.toFixed(2)}</span>
    </div>
    <div class="result-row total">
      <span>المتبقي</span>
      <span>${remaining.toFixed(2)}</span>
    </div>
    </div>

    <script>
    window.onload = function() { window.print(); };
    </script>
    </body>
    </html>`;

    printWindow.document.write(html);
    printWindow.document.close();
  };

  const columns = [
    {
      title: 'معرف',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: 'رقم الفاتورة',
      dataIndex: 'number',
      key: 'number',
    },
    {
      title: 'العميل/المورد',
      dataIndex: 'clientAccountId',
      key: 'clientAccountId',
      render: (clientAccountId) => {
        const account = accounts.find(acc => acc.id === clientAccountId);
        return account ? account.name : '-';
      },
    },
    {
      title: 'تاريخ الإنشاء',
      dataIndex: 'createdDate',
      key: 'createdDate',
      render: (date) => date ? dayjs(date).format('YYYY-MM-DD') : '',
    },
    {
      title: 'القيمة',
      dataIndex: 'value',
      key: 'value',
      render: (value) => `$${value}`,
    },
    {
      title: 'إجراءات',
      key: 'actions',
      render: (text, record) => (
        <Space size="middle">
          <Button
            type="link"
            icon={<PrinterOutlined />}
            onClick={() => handlePrint(record)}
          />
          <Button 
            type="link" 
            icon={<EditOutlined />} 
            onClick={() => onEdit(record)} 
          />
          <Popconfirm
            title="هل أنت متأكد من حذف هذه الفاتورة؟"
            onConfirm={() => onDelete(record.id)}
            okText="نعم"
            cancelText="لا"
          >
            <Button type="link" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="table-responsive-wrapper">
      <Table
        columns={columns}
        dataSource={dataSource}
        rowKey="id"
        loading={loading}
        scroll={{ x: 'max-content' }}
      />
    </div>
  );
};

export default InvoiceTable;
